// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,       setUser]       = useState(null);
  const [token,      setToken]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  /* ── Rehydrate from localStorage on mount ── */
  useEffect(() => {
    try {
      const storedToken = localStorage.getItem('token');
      const storedUser  = localStorage.getItem('user');
      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        if (parsedUser.profileComplete === false) {
          setNeedsSetup(true);
        }
      }
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, []);

  /* ── Internal helper: persist session ── */
  const _persist = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  /* ── Update stored user ── */
  const setUserData = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  /* ── Email / Password Login ── */
  const login = useCallback(async (identifier, password) => {
    try {
      const data = await authService.login(identifier, password);
      if (data.success) {
        _persist(data.user, data.token);
        setNeedsSetup(false);
      }
      return data;
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Login failed. Please try again.',
      };
    }
  }, []);

  /* ── Google Login / Register ── */
  const googleLogin = useCallback(async (accessToken) => {
    try {
      const data = await authService.googleAuth(accessToken);
      if (data.success) {
        _persist(data.user, data.token);
        setNeedsSetup(data.isNewUser === true);
      }
      return data;
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Google login failed. Please try again.',
      };
    }
  }, []);

  /* ── Complete Google setup (username + password) ──
     Token is now auto-attached by the api.js interceptor,
     so no manual token passing needed here.
  ── */
  const completeGoogleSetup = useCallback(async ({ username, password }) => {
    try {
      const data = await authService.completeGoogleSetup({ username, password });
      if (data.success) {
        setUserData(data.user);
        setNeedsSetup(false);
      }
      return data;
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Setup failed. Please try again.',
      };
    }
  }, [setUserData]);

  /* ── OTP-verified Registration ── */
  const register = useCallback(async ({ fullName, username, email, password, otp }) => {
    try {
      const data = await authService.register({ fullName, username, email, password, otp });
      if (data.success) {
        _persist(data.user, data.token);
        setNeedsSetup(false);
      }
      return data;
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Registration failed. Please try again.',
      };
    }
  }, []);

  /* ── Logout ── */
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setNeedsSetup(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, []);

  /* ── ★ FIX: react to the 401 event dispatched by api.js's response
     interceptor, so isAuthenticated flips false the moment the server
     tells us the token is dead, instead of trusting localStorage forever. ── */
  useEffect(() => {
    window.addEventListener('auth:unauthorized', logout);
    return () => window.removeEventListener('auth:unauthorized', logout);
  }, [logout]);

  /* ── Update profile ── */
  const updateProfile = useCallback(async (updates) => {
    try {
      const data = await authService.updateProfile(updates);
      if (data.success) {
        const updatedUser = { ...user, ...updates };
        setUserData(updatedUser);
      }
      return data;
    } catch {
      return { success: false, message: 'Network error' };
    }
  }, [user, setUserData]);

  const isAuthenticated = !!token && !!user;

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    needsSetup,
    login,
    googleLogin,
    completeGoogleSetup,
    register,
    logout,
    updateProfile,
    setUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within <AuthProvider>.');
  }
  return ctx;
}
